const SIZE = 6;
const BOX_ROWS = 2;
const BOX_COLS = 3;

export function shuffle<T>(items: T[]): T[] {
  const result = [...items];

  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result;
}

export function createBaseSolution(): number[][] {
  const grid: number[][] = [];

  for (let row = 0; row < SIZE; row += 1) {
    for (let col = 0; col < SIZE; col += 1) {
      const value = (row * BOX_COLS + Math.floor(row / BOX_ROWS) + col) % SIZE;

      if (!grid[row]) {
        grid[row] = [];
      }

      grid[row][col] = value + 1;
    }
  }

  return grid;
}

export function generateSolution(): number[] {
  let grid = createBaseSolution();

  const numbers = shuffle([1, 2, 3, 4, 5, 6]);

  grid = grid.map((row) => row.map((value) => numbers[value - 1]));

  const rowBands = [
    [0, 1],
    [2, 3],
    [4, 5],
  ];

  const rows: number[][] = [];

  shuffle(rowBands).forEach((band) => {
    shuffle(band).forEach((rowIndex) => {
      rows.push(grid[rowIndex]);
    });
  });

  grid = rows;

  const colStacks = [
    [0, 1, 2],
    [3, 4, 5],
  ];

  const columns: number[] = [];

  shuffle(colStacks).forEach((stack) => {
    shuffle(stack).forEach((colIndex) => {
      columns.push(colIndex);
    });
  });

  grid = grid.map((row) => columns.map((col) => row[col]));

  return grid.flat();
}

export function isValidMove(grid: number[], index: number, value: number): boolean {
  const row = Math.floor(index / SIZE);
  const col = index % SIZE;

  for (let c = 0; c < SIZE; c += 1) {
    const i = row * SIZE + c;

    if (i !== index && grid[i] === value) {
      return false;
    }
  }

  for (let r = 0; r < SIZE; r += 1) {
    const i = r * SIZE + col;

    if (i !== index && grid[i] === value) {
      return false;
    }
  }

  const startRow = Math.floor(row / BOX_ROWS) * BOX_ROWS;
  const startCol = Math.floor(col / BOX_COLS) * BOX_COLS;

  for (let r = startRow; r < startRow + BOX_ROWS; r += 1) {
    for (let c = startCol; c < startCol + BOX_COLS; c += 1) {
      const i = r * SIZE + c;

      if (i !== index && grid[i] === value) {
        return false;
      }
    }
  }

  return true;
}

export function countSolutions(grid: number[], limit = 2): number {
  let count = 0;

  const solve = () => {
    if (count >= limit) {
      return;
    }

    let emptyIndex = -1;

    for (let i = 0; i < grid.length; i += 1) {
      if (grid[i] === 0) {
        emptyIndex = i;
        break;
      }
    }

    if (emptyIndex === -1) {
      count += 1;
      return;
    }

    for (let value = 1; value <= SIZE; value += 1) {
      if (isValidMove(grid, emptyIndex, value)) {
        grid[emptyIndex] = value;
        solve();
        grid[emptyIndex] = 0;
      }
    }
  };

  solve();
  return count;
}

export function generatePuzzle(
  clueCount: number,
): { solution: number[]; puzzle: number[] } {
  for (let attempt = 0; attempt < 100; attempt += 1) {
    const generatedSolution = generateSolution();
    const puzzle = [...generatedSolution];
    const positions = shuffle(Array.from({ length: SIZE * SIZE }, (_, index) => index));
    const removeCount = SIZE * SIZE - clueCount;

    let removed = 0;

    for (const position of positions) {
      if (removed >= removeCount) {
        break;
      }

      const backup = puzzle[position];
      puzzle[position] = 0;

      const solutions = countSolutions([...puzzle], 2);

      if (solutions !== 1) {
        puzzle[position] = backup;
      } else {
        removed += 1;
      }
    }

    if (removed >= removeCount) {
      return {
        solution: generatedSolution,
        puzzle,
      };
    }
  }

  const fallback = generateSolution();
  const puzzle = [...fallback];
  const positions = shuffle(Array.from({ length: SIZE * SIZE }, (_, index) => index));

  for (let i = 0; i < SIZE * SIZE - clueCount; i += 1) {
    puzzle[positions[i]] = 0;
  }

  return {
    solution: fallback,
    puzzle,
  };
}
